import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common';

/**
 * A resize request emitted by the child iframe via the bridge.
 *
 * Either or both axes may be present.
 */
export type ChildResizeRequest = {
	height?: number;
	width?: number;
};

/**
 * Per-axis policy for accepting a child resize request.
 *
 * Each flag toggles whether the corresponding axis is gated once the host has
 * already persisted a dimension on that axis. When gated, child requests on a
 * persisted axis are dropped — i.e. the dimension is only ever set on the
 * initial child-driven resize and cannot be changed by subsequent child
 * requests afterwards.
 */
export type ChildResizePolicy = {
	gateHeight: boolean;
	gateWidth: boolean;
};

/**
 * Whether `key` has been EXPLICITLY persisted in the embed's macroParams.
 *
 * We deliberately do not use `getParameters(...)` here — that helper falls
 * back to `NATIVE_EMBED_PARAMETER_DEFAULTS`, so an unset axis would still
 * appear non-null and the gate would reject every request.
 */
const isAxisPersisted = (
	params: NativeEmbedParameters | undefined,
	key: 'width' | 'height',
): boolean => params?.macroParams?.[key] !== undefined;

/**
 * Decide which axes of a child-initiated resize request to apply, given the
 * embed's currently persisted ADF parameters.
 *
 * The host's ADF parameters are the authoritative source of truth for what
 * has been committed — derived layout values (e.g. heights synthesised from
 * an aspectRatio) MUST NOT be used here, as they would cause the gate to
 * reject every request on otherwise-unpersisted axes.
 *
 * Pure / referentially transparent: no side effects, no DOM/PM access.
 */
export function resolveChildResize(
	params: NativeEmbedParameters | undefined,
	request: ChildResizeRequest,
	policy: ChildResizePolicy,
): ChildResizeRequest {
	return {
		width: policy.gateWidth && isAxisPersisted(params, 'width') ? undefined : request.width,
		height: policy.gateHeight && isAxisPersisted(params, 'height') ? undefined : request.height,
	};
}
